import { createContext, FC, useContext, useState, useEffect } from "react";
import {
  DiscountByCodeQuery,
  GetAttendeePageCustomerQuery,
  GetProductGroupPageDetailQuery,
  TransactionType,
  useDeleteShoppingCartMutation,
  useGetClientPaymentMethodsManyLazyQuery,
} from "src/generated/graphql";
import { CartType, ShoppingCartMethods } from "src/utils/constants";
import { useAppContext } from "../AppContext";
import { ISelectedPayment } from "src/components/PaymentSelection";
import { ArrayElement } from "src/utils/helperTypes";

export enum UnsavedMethod {
  Card = "New Card",
  Terminal = "Card Terminal",
  ACH = "New Bank Account",
  Cash = "Cash Payment",
}

export type IProductCart = IProductsCatalog[0] & { count: number };

export type IProductGroup =
  GetProductGroupPageDetailQuery["productGroupPage"]["items"][0];

export type IProductsCatalog = IProductGroup["products"];
export type IDiscountByCode = DiscountByCodeQuery["discountByCode"];

export type CatalogContextData = {
  sortRevenue: string;
  setSortRevenue: (e: string) => void;
  count: number;
  activePage: number;
  setPage: (e: number) => void;
  search: string;
  setSearch: (e: string) => void;
  productsCart: IProductCart[];
  setProductsCart: (e: IProductCart[]) => void;
  handleSortChange: (id: string) => void;
  handleCartUpdate: (
    product: ArrayElement<IProductsCatalog> | IProductCart,
    cartType: CartType
  ) => void;
  selectedPG: IProductGroup | null;
  setSelectedPG: (e: IProductGroup | null) => void;
  handleBack: () => void;
  selectedProduct: IProductsCatalog[0] | null;
  handleProductSelection: (product: IProductsCatalog[0]) => void;
  metricValues: Record<string, string[]>;
  handleVariantSelection: (metric: string, value: string) => void;
  handleCheckout: () => void;
  activeScreen: Screens;
  setactiveScreen: (e: Screens) => void;

  selectedCustomer: IAttendeeSelect;
  setselectedCustomer: (e: IAttendeeSelect) => void;
  selectedAttendeeId: string;
  setselectedAttendeeId: (e: string) => void;

  selectedTerminalId: string;
  setselectedTerminalId: (e: string) => void;
  selectedUnsavedMethod: UnsavedMethod | undefined;
  setSelectedUnsavedMethod: (e: UnsavedMethod | undefined) => void;
  selectedPayment: ISelectedPayment | null;
  setselectedPayment: (e: ISelectedPayment | null) => void;
  transactionType: TransactionType | undefined;
  setTransactionType: (e: TransactionType | undefined) => void;
  appliedCodes: IDiscountByCode[];
  setappliedCodes: (e: IDiscountByCode[]) => void;
  resetState: () => void;
};
export const CatalogContext = createContext<CatalogContextData>(
  {} as CatalogContextData
);

export const useCatalogContext = () => {
  const context = useContext(CatalogContext);
  if (!context) {
    throw new Error("useCatalogContext must be used within a CatalogProvider");
  }
  return context;
};

interface Props {
  children: React.ReactNode;
}

export enum Screens {
  Main,
  Checkout,
}

export type IAttendeeSelect =
  | GetAttendeePageCustomerQuery["attendeePage"]["items"][0]
  | null;

const CatalogProvider: FC<Props> = ({ children }) => {
  const { currentLocation } = useAppContext();
  const count = 10;
  const [sortRevenue, setSortRevenue] =
    useState<CatalogContextData["sortRevenue"]>("");
  const [activePage, setPage] = useState<CatalogContextData["activePage"]>(1);
  const [selectedPG, setSelectedPG] =
    useState<CatalogContextData["selectedPG"]>(null);
  const [search, setSearch] = useState<CatalogContextData["search"]>("");
  const [productsCart, setProductsCart] = useState<
    CatalogContextData["productsCart"]
  >([]);
  const [activeScreen, setactiveScreen] = useState<
    CatalogContextData["activeScreen"]
  >(Screens.Main);
  const [selectedAttendeeId, setselectedAttendeeId] =
    useState<CatalogContextData["selectedAttendeeId"]>("");
  const [selectedCustomer, setselectedCustomer] =
    useState<CatalogContextData["selectedCustomer"]>(null);
  const [selectedPayment, setselectedPayment] =
    useState<CatalogContextData["selectedPayment"]>(null);
  const [selectedTerminalId, setselectedTerminalId] =
    useState<CatalogContextData["selectedTerminalId"]>("");
  const [selectedUnsavedMethod, setSelectedUnsavedMethod] =
    useState<CatalogContextData["selectedUnsavedMethod"]>();
  const [transactionType, setTransactionType] =
    useState<CatalogContextData["transactionType"]>(undefined);
  const [appliedCodes, setappliedCodes] = useState<
    CatalogContextData["appliedCodes"]
  >([]);

  const [selectedProduct, setSelectedProduct] =
    useState<CatalogContextData["selectedProduct"]>(null);
  const [metricValues, setMetricValues] = useState<
    CatalogContextData["metricValues"]
  >({});

  const resetState = () => {
    setactiveScreen(Screens.Main);
    setSelectedPG(null);
    setProductsCart([]);
    setselectedAttendeeId("");
    setselectedCustomer(null);
    setselectedPayment(null);
    setselectedTerminalId("");
    setSelectedUnsavedMethod(undefined);
    setappliedCodes([]);
    ShoppingCartMethods.remove();
  };

  const [getPayments] = useGetClientPaymentMethodsManyLazyQuery({
    onCompleted(data) {
      let paymentPrimary = data.clientPaymentMethodsMany?.find(
        (p) => p.isPrimary
      );
      paymentPrimary && setselectedPayment(paymentPrimary);
    },
  });

  const [deleteShoppingCart] = useDeleteShoppingCartMutation();

  useEffect(() => {
    // selectedCustomer change reset payment and select primary payment method
    setselectedAttendeeId("");
    setselectedPayment(null);
    selectedCustomer?.client?.id &&
      getPayments({ variables: { clientId: selectedCustomer.client.id } });
  }, [selectedCustomer]);

  useEffect(() => {
    // currentLocation change reset states
    resetState();
  }, [currentLocation]);

  const handleSortChange = (id: string) => {
    setSortRevenue(sortRevenue === id || id === "" ? "" : id);
  };

  const handleCartUpdate = (
    product: IProductCart | IProductsCatalog[0],
    cartType: CartType
  ) => {
    const indexToUpdate = productsCart.findIndex((p) => p.id === product.id);
    let _productsCart = productsCart;

    switch (cartType) {
      case CartType.add:
        if ("count" in product && indexToUpdate > -1) {
          _productsCart[indexToUpdate].count! += 1;
          _productsCart = [..._productsCart];
        } else {
          _productsCart = [
            ...productsCart,
            {
              count: 1,
              ...product,
            },
          ];
        }
        setProductsCart(_productsCart);
        setSelectedProduct(null);
        break;

      case CartType.subtract:
        if ("count" in product && product.count === 1) {
          _productsCart = _productsCart.filter((p) => p.id !== product.id);
          setProductsCart(_productsCart);
        } else {
          _productsCart[indexToUpdate].count! -= 1;
          _productsCart = [..._productsCart];
        }
        setProductsCart(_productsCart);
        break;
      default:
        break;
    }
  };

  const handleBack = () => {
    if (activeScreen === Screens.Checkout) {
      const _shoppingCartId = ShoppingCartMethods.get();
      setactiveScreen(Screens.Main);
      if (_shoppingCartId) {
        deleteShoppingCart({
          variables: {
            id: _shoppingCartId,
          },
        });
        ShoppingCartMethods.remove();
        setappliedCodes([]);
        return;
      }
    }
    setSelectedPG(null);
    setSelectedProduct(null);
    setMetricValues({});
  };

  const handleProductSelection = (product: IProductsCatalog[0]) => {
    setSelectedProduct(product.id === selectedProduct?.id ? null : product);
  };

  const handleVariantSelection = (metric: string, value: string) => {
    const isPresent = metricValues[metric]?.includes(value);
    if (isPresent) {
      metricValues[metric] = metricValues[metric].filter((v) => v !== value);
      metricValues[metric].length === 0 && delete metricValues[metric];
    } else {
      metricValues[metric] = [...(metricValues[metric] || []), value];
    }
    setMetricValues({ ...metricValues });
  };

  const handleCheckout = () => {
    setactiveScreen(Screens.Checkout);
  };

  return (
    <CatalogContext.Provider
      value={{
        setSortRevenue,
        sortRevenue,
        count,
        activePage,
        setPage,
        search,
        setSearch,
        handleSortChange,
        productsCart,
        setProductsCart,
        handleCartUpdate,
        selectedPG,
        setSelectedPG,
        selectedProduct,
        handleBack,
        handleProductSelection,
        metricValues,
        handleVariantSelection,
        handleCheckout,
        activeScreen,
        setactiveScreen,
        selectedCustomer,
        setselectedCustomer,
        selectedAttendeeId,
        setselectedAttendeeId,
        selectedUnsavedMethod,
        setSelectedUnsavedMethod,
        selectedPayment,
        setselectedPayment,
        selectedTerminalId,
        setselectedTerminalId,
        appliedCodes,
        setappliedCodes,
        transactionType,
        setTransactionType,
        resetState,
      }}
    >
      {children}
    </CatalogContext.Provider>
  );
};

export default CatalogProvider;
